import { User } from '../../../types/User';
import { showAlert } from '@silverfort/sf-components';
import { useCallback } from 'react';
import { useUpdateUser, useUpdateUsers } from '../../../api';
import useExcludePopup from '../../../hooks/useExcludePopup';

interface UseExcludeUserPopupParams {
    user: User;
    onUserExcluded?: () => void;
}

interface UseExcludeUsersPopupParams {
    users: User[];
    onUsersExcluded?: () => void;
}

interface ReturnType {
    openExcludeUserPopup: () => void;
}

export const useExcludeUserPopup = ({ user, onUserExcluded }: UseExcludeUserPopupParams): ReturnType => {
    const handleUserExcluded = useCallback(() => {
        onUserExcluded?.();
    }, [onUserExcluded]);

    const handleError = useCallback(() => {
        showAlert('Failed to exclude user', 'error');
    }, []);

    const { updateUser } = useUpdateUser({
        onSuccess: handleUserExcluded,
        onError: handleError
    });

    const handleExclude = useCallback(
        (excludeTill: Date) => {
            updateUser(user.guid, { excludeTill });
        },
        [user.guid, updateUser]
    );

    const { openExcludePopup } = useExcludePopup({
        title: 'Exclude users',
        description: `How long do you want to exclude ${user.displayName}?`,
        customExcludeDescription: 'How long do you want to exclude the selected users?',
        onExclude: handleExclude
    });

    return { openExcludeUserPopup: openExcludePopup };
};

const getExcludeUsersDescription = (users: User[]): string => {
    if (!users.length) {
        return '';
    }

    if (users.length > 1) {
        return 'How long do you want to exclude the selected users?';
    }

    return `How long do you want to exclude ${users[0]?.displayName}?`;
};

export const useExcludeUsersPopup = ({ users, onUsersExcluded }: UseExcludeUsersPopupParams): ReturnType => {
    const handleUsersExcluded = useCallback(() => {
        onUsersExcluded?.();
        showAlert(`${users.length > 1 ? users.length + ' users' : users[0].displayName} excluded successfully`, 'success');
    }, [onUsersExcluded, users]);

    const handleError = useCallback(() => {
        showAlert('Failed to exclude users, please try again', 'error');
    }, []);

    const { updateUsers } = useUpdateUsers({
        onSuccess: handleUsersExcluded,
        onError: handleError
    });

    const handleExclude = useCallback(
        (excludeTill: Date) => {
            updateUsers(users.map(({ guid }) => ({ guid, excludeTill })));
        },
        [users, updateUsers]
    );

    const { openExcludePopup } = useExcludePopup({
        title: 'Exclude users',
        description: getExcludeUsersDescription(users),
        customExcludeDescription: 'How long do you want to exclude the selected users?',
        onExclude: handleExclude
    });

    return { openExcludeUserPopup: openExcludePopup };
};
